import { useCallback, useSyncExternalStore } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { queryClient } from "./use-query-client";

/**
 * Reactively read data that lives in the query cache but is populated via
 * `setQueryData` (i.e. has no `queryFn`) — e.g. MEMBERS / DEPENDENTS, which are
 * written as a side effect of the demographics query, and DATA_ACCESS_LIST.
 *
 * Unlike `queryClient.getQueryData()` this subscribes to cache changes, so the
 * component re-renders when the value lands instead of freezing a one-time
 * snapshot. Unlike `useQuery` it never triggers a fetch. It reads the shared
 * `queryClient` singleton (the same instance the app writes to), not the React
 * context client.
 *
 * The returned value is referentially stable until the cached value actually
 * changes, so it is safe to use in effect dependency arrays.
 */
export function useCachedQueryData<T>(queryKey: QueryKey): T | undefined {
  const queryHash = JSON.stringify(queryKey);

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe(onStoreChange),
    [],
  );

  const getSnapshot = useCallback(
    () => queryClient.getQueryData<T>(queryKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryHash],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}
